import { Fragment, memo } from 'react';
import { FormattedMessage } from 'react-intl';
import { Button, HStack, TableCell, TableRow, Text, Tooltip } from '@library';
import type { ProductVehicle } from '../../../types';
import styles from './ProductCompatibilityVehicleRow.module.scss';

const ProductCompatibilityVehicleRow = ({
    vehicle,
    showTypeColumn,
    isProductCompatibilityTable = false,
    onModelChange,
}: {
    vehicle: ProductVehicle;
    showTypeColumn?: boolean;
    isProductCompatibilityTable?: boolean;
    onModelChange: (vehicleId: string) => void;
}) => {
    const limit = 4;

    const firstFewTypeAcceptances = vehicle.typeAcceptanceList.slice(0, limit);
    const remainingTypeAcceptances = vehicle.typeAcceptanceList.slice(limit);

    const firstFewTypeNumbers = vehicle.typeNumbers.slice(0, limit);
    const remainingTypeNumbers = vehicle.typeNumbers.slice(limit);

    return (
        <TableRow
            role="link"
            hoverEffect
            onPress={() => onModelChange(vehicle.id)}
            height={isProductCompatibilityTable ? 'auto' : '80px'}
        >
            <TableCell>
                {isProductCompatibilityTable ? (
                    <span className={styles.highlightOnHover}>
                        {vehicle.manufacturerName}
                    </span>
                ) : (
                    vehicle.manufacturerName
                )}
            </TableCell>
            <TableCell>
                {isProductCompatibilityTable ? (
                    <span className={styles.highlightOnHover}>
                        {vehicle.model}
                    </span>
                ) : (
                    vehicle.model
                )}
            </TableCell>
            <TableCell>{vehicle.ccm}</TableCell>
            <TableCell>{vehicle.ratedPower}</TableCell>
            <TableCell>
                {vehicle.manufacturingStartYear} -{' '}
                {vehicle.manufacturingEndYear || ''}
            </TableCell>
            {!!showTypeColumn && (
                <TableCell>
                    <HStack wrap>
                        {firstFewTypeNumbers.map((typeNumber, idx) => (
                            <Fragment key={typeNumber}>
                                <Text secondary regular>
                                    {typeNumber}
                                </Text>
                                {idx !== firstFewTypeNumbers.length - 1 && (
                                    <>,&nbsp;</>
                                )}
                            </Fragment>
                        ))}
                        {remainingTypeNumbers.length > 0 && <>, </>}
                        {remainingTypeNumbers.length > 0 && (
                            <Tooltip
                                mode="click"
                                content={
                                    <HStack wrap gap="mini">
                                        {remainingTypeNumbers.map(item => (
                                            <Text key={item} secondary regular>
                                                {item}
                                            </Text>
                                        ))}
                                    </HStack>
                                }
                                showArrow
                                modal
                            >
                                <div>
                                    &nbsp;
                                    <Button text inline>
                                        <Text secondary>
                                            <FormattedMessage
                                                id="+{count} more"
                                                values={{
                                                    count: remainingTypeNumbers.length,
                                                }}
                                            />
                                        </Text>
                                    </Button>
                                </div>
                            </Tooltip>
                        )}
                    </HStack>
                </TableCell>
            )}
            <TableCell>
                <HStack wrap>
                    {firstFewTypeAcceptances.map((typeAcceptance, idx) => (
                        <Fragment key={typeAcceptance}>
                            <Text secondary regular>
                                {typeAcceptance}
                            </Text>
                            {idx !== firstFewTypeAcceptances.length - 1 && (
                                <>,&nbsp;</>
                            )}
                        </Fragment>
                    ))}

                    {remainingTypeAcceptances.length > 0 && <>, </>}
                    {remainingTypeAcceptances.length > 0 && (
                        <Tooltip
                            mode="click"
                            content={
                                <HStack wrap gap="mini">
                                    {remainingTypeAcceptances.map(item => (
                                        <Text key={item} secondary regular>
                                            {item}
                                        </Text>
                                    ))}
                                </HStack>
                            }
                            showArrow
                            modal
                        >
                            <div>
                                &nbsp;
                                <Button text inline>
                                    <Text secondary>
                                        <FormattedMessage
                                            id="+{count} more"
                                            values={{
                                                count: remainingTypeAcceptances.length,
                                            }}
                                        />
                                    </Text>
                                </Button>
                            </div>
                        </Tooltip>
                    )}
                </HStack>
            </TableCell>
        </TableRow>
    );
};

export default memo(ProductCompatibilityVehicleRow);
